/**
 * MockDataTab
 *
 * NodeEditor tab that lets the user inspect and edit the per-node mock state
 * stored in `workflowStore.mockedNodes`. The same component handles both
 * the trigger entry (a bare `TriggerMockPayload`) and per-action entries
 * (`MockNodeInfo` with `mockOutput`).
 *
 * Valid edits are written to `mockedNodes`, which is what `useTriggerMockRun`
 * ships to the `submitDraftRun` API. Raw text and validation stay local to
 * each editor; invalid edits leave the last accepted value unchanged.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  Dropdown,
  Field,
  Input,
  Option,
  Switch,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@coreai/fluentui-react';
import { JsonEditor } from '@/components/WorkflowDesigner/JsonEditor/JsonEditor';
import { useWorkflowStore } from '@/stores/workflowStore';
import { visuallyHiddenStyle } from '@/styles/a11y';
import type {
  MockDefinition,
  MockNodeInfo,
  TriggerMockPayload,
} from '@/types/templateMode';

const MOCK_STATUSES = ['Succeeded', 'Failed', 'Skipped', 'TimedOut', 'Cancelled'] as const;
const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'] as const;

const INVALID_MOCK_JSON_MESSAGE = 'Invalid JSON. Your changes have not been applied.';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    flex: 1,
    minHeight: 0,
  },
  row: {
    display: 'flex',
    columnGap: tokens.spacingHorizontalM,
    alignItems: 'flex-end',
  },
  rowItem: {
    flex: 1,
    minWidth: 0,
  },
  jsonField: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: '120px',
  },
  jsonFieldSmall: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '200px',
  },
  jsonContainer: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
  },
  helper: {
    color: tokens.colorNeutralForeground3,
  },
  toggleRow: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  intro: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
  },
  heading: {
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  srOnly: visuallyHiddenStyle,
});

// =============================================================================
// Helpers
// =============================================================================

/** Parse JSON; return undefined on failure so the previous value is kept. */
function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/** Render undefined as blank editor text; preserve explicit null as "null". */
function toEditorJson(value: unknown): string {
  if (value === undefined) return '';
  return JSON.stringify(value, null, 2);
}

function emptyMockOutput(): MockDefinition {
  return { status: 'Succeeded', outputs: { statusCode: '200' } };
}

// =============================================================================
// JSON field
// =============================================================================

interface MockJsonFieldProps {
  label: string;
  testId: string;
  value: string;
  onChange: (text: string) => void;
  className: string;
}

function MockJsonField({ label, testId, value, onChange, className }: MockJsonFieldProps): JSX.Element {
  const styles = useStyles();
  const [editedText, setEditedText] = useState<string>();
  const text = editedText ?? value;
  const invalid = text.trim() !== '' && safeParse(text) === undefined;

  const handleChange = (next: string) => {
    setEditedText(next);
    onChange(next);
  };

  return (
    <Field
      label={label}
      className={className}
      data-testid={testId}
      // Announcements come from the field's stable live region, so the
      // message itself is not an alert (avoids double announcements).
      validationMessage={invalid ? { children: INVALID_MOCK_JSON_MESSAGE, role: undefined } : null}
    >
      {(controlProps) => (
        <div className={styles.jsonContainer}>
          <JsonEditor
            value={text}
            onChange={handleChange}
            fill
            hasError={invalid}
            ariaLabelledBy={controlProps['aria-labelledby']}
            ariaDescribedBy={controlProps['aria-describedby']}
            ariaInvalid={invalid}
          />
          <div role="status" aria-live="polite" aria-atomic="true" className={styles.srOnly}>
            {invalid ? `${label}: ${INVALID_MOCK_JSON_MESSAGE}` : ''}
          </div>
        </div>
      )}
    </Field>
  );
}

// =============================================================================
// MockDataTab
// =============================================================================

export interface MockDataTabProps {
  /** Canvas-prefixed node id (`trigger-<name>` or `action-<scoped>`). */
  nodeId: string;
}

export function MockDataTab({ nodeId }: MockDataTabProps): JSX.Element {
  const entry = useWorkflowStore((s) => s.mockedNodes[nodeId]);
  const setMockedNode = useWorkflowStore((s) => s.setMockedNode);
  const setMockNodeEnabled = useWorkflowStore((s) => s.setMockNodeEnabled);

  if (nodeId.startsWith('trigger-')) {
    return (
      <TriggerMockForm
        key={nodeId}
        nodeId={nodeId}
        entry={entry as TriggerMockPayload | undefined}
        onChange={setMockedNode}
      />
    );
  }
  return (
    <ActionMockForm
      key={nodeId}
      nodeId={nodeId}
      entry={entry as MockNodeInfo | undefined}
      onChange={setMockedNode}
      onToggleEnabled={setMockNodeEnabled}
    />
  );
}

// =============================================================================
// Action editor
// =============================================================================

interface ActionMockFormProps {
  nodeId: string;
  entry: MockNodeInfo | undefined;
  onChange: (nodeId: string, info: MockNodeInfo) => void;
  onToggleEnabled: (nodeId: string, enabled: boolean) => void;
}

function ActionMockForm({ nodeId, entry, onChange, onToggleEnabled }: ActionMockFormProps): JSX.Element {
  const styles = useStyles();
  const actionName = entry?.actionName ?? nodeId.replace(/^action-/, '');
  const enabled = entry?.enabled !== false;
  const mockOutput = useMemo<MockDefinition>(
    () => entry?.mockOutput ?? emptyMockOutput(),
    [entry?.mockOutput],
  );

  const writeMock = useCallback(
    (next: MockDefinition) => {
      onChange(nodeId, {
        actionName,
        mockOutput: next,
        // Preserve the per-action toggle across edits.
        ...(entry?.enabled !== undefined ? { enabled: entry.enabled } : {}),
      });
    },
    [nodeId, actionName, onChange, entry?.enabled],
  );

  const handleStatusChange = useCallback(
    (status: string) => writeMock({ ...mockOutput, status }),
    [mockOutput, writeMock],
  );

  const handleStatusCodeChange = useCallback(
    (statusCode: string) =>
      writeMock({ ...mockOutput, outputs: { ...mockOutput.outputs, statusCode } }),
    [mockOutput, writeMock],
  );

  const handleBodyChange = useCallback(
    (text: string) => {
      if (text.trim() === '') {
        const { body: _ignored, ...restOutputs } = mockOutput.outputs;
        writeMock({ ...mockOutput, outputs: restOutputs });
        return;
      }
      const parsed = safeParse(text);
      if (parsed !== undefined) {
        writeMock({ ...mockOutput, outputs: { ...mockOutput.outputs, body: parsed } });
      }
    },
    [mockOutput, writeMock],
  );

  const handleHeadersChange = useCallback(
    (text: string) => {
      const parsed = safeParse(text);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        writeMock({
          ...mockOutput,
          outputs: { ...mockOutput.outputs, headers: parsed as Record<string, string> },
        });
      }
    },
    [mockOutput, writeMock],
  );

  return (
    <div className={styles.root}>
      <div className={styles.toggleRow}>
        <Switch
          checked={enabled}
          label={enabled ? 'Test data on' : 'Test data off'}
          onChange={(_, data) => onToggleEnabled(nodeId, data.checked)}
        />
        <Text className={styles.helper}>
          When on, this action returns the test output below instead of running
          live. When off, it runs normally and shows its connection status.
        </Text>
      </div>
      <div className={styles.row}>
        <Field label="Status" className={styles.rowItem}>
          <Dropdown
            value={mockOutput.status}
            selectedOptions={[mockOutput.status]}
            onOptionSelect={(_, data) => {
              if (data.optionValue) handleStatusChange(data.optionValue);
            }}
          >
            {MOCK_STATUSES.map((s) => (
              <Option key={s} value={s}>
                {s}
              </Option>
            ))}
          </Dropdown>
        </Field>
        <Field label="Status code" className={styles.rowItem}>
          <Input
            value={mockOutput.outputs.statusCode ?? ''}
            onChange={(_, data) => handleStatusCodeChange(data.value)}
          />
        </Field>
      </div>

      <MockJsonField
        label="Body (JSON)"
        testId="mock-data-body"
        className={styles.jsonField}
        value={toEditorJson(mockOutput.outputs.body)}
        onChange={handleBodyChange}
      />

      <MockJsonField
        label="Headers (JSON)"
        testId="mock-data-headers"
        className={styles.jsonFieldSmall}
        value={JSON.stringify(mockOutput.outputs.headers ?? {}, null, 2)}
        onChange={handleHeadersChange}
      />
    </div>
  );
}

// =============================================================================
// Trigger editor
// =============================================================================

interface TriggerMockFormProps {
  nodeId: string;
  entry: TriggerMockPayload | undefined;
  onChange: (nodeId: string, info: TriggerMockPayload) => void;
}

// No on/off toggle here, unlike actions: the trigger payload is always sent
// with a mock run (`triggerMockWorkflowRun` has no "live" variant for the
// trigger block), so there is nothing to toggle.
function TriggerMockForm({ nodeId, entry, onChange }: TriggerMockFormProps): JSX.Element {
  const styles = useStyles();
  const method = entry?.method ?? 'POST';

  const write = useCallback(
    (next: TriggerMockPayload) => onChange(nodeId, next),
    [nodeId, onChange],
  );

  const handleMethodChange = useCallback(
    (next: string) => {
      if (entry?.body === undefined) {
        write({ ...entry, method: next });
        return;
      }
      write({ ...entry, method: next, body: entry.body });
    },
    [entry, write],
  );

  const handleHeadersChange = useCallback(
    (text: string) => {
      const parsed = safeParse(text);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        write({ ...entry, method, headers: parsed as Record<string, string> });
      }
    },
    [entry, method, write],
  );

  const handleQueriesChange = useCallback(
    (text: string) => {
      const parsed = safeParse(text);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        write({ ...entry, method, queries: parsed as Record<string, string> });
      }
    },
    [entry, method, write],
  );

  const handleBodyChange = useCallback(
    (text: string) => {
      if (text.trim() === '') {
        const { body: _ignored, ...rest } = (entry ?? { method }) as TriggerMockPayload;
        write({ ...rest, method });
        return;
      }
      const parsed = safeParse(text);
      if (parsed !== undefined) {
        write({ ...entry, method, body: parsed });
      }
    },
    [entry, method, write],
  );

  return (
    <div className={styles.root}>
      <div className={styles.intro}>
        <Text as="h3" className={styles.heading}>
          Draft trigger input
        </Text>
        <Text className={styles.helper}>
          Runs with test data use the trigger input below. Edit these fields to
          replace it. Automatic runs of published workflows use their configured
          triggers.
        </Text>
      </div>
      <Field label="Method">
        <Dropdown
          value={method}
          selectedOptions={[method]}
          onOptionSelect={(_, data) => {
            if (data.optionValue) handleMethodChange(data.optionValue);
          }}
        >
          {HTTP_METHODS.map((m) => (
            <Option key={m} value={m}>
              {m}
            </Option>
          ))}
        </Dropdown>
      </Field>

      <MockJsonField
        label="Body (JSON)"
        testId="mock-data-body"
        className={styles.jsonField}
        value={toEditorJson(entry?.body)}
        onChange={handleBodyChange}
      />

      <MockJsonField
        label="Headers (JSON)"
        testId="mock-data-headers"
        className={styles.jsonFieldSmall}
        value={JSON.stringify(entry?.headers ?? {}, null, 2)}
        onChange={handleHeadersChange}
      />

      <MockJsonField
        label="Query parameters (JSON)"
        testId="mock-data-queries"
        className={styles.jsonFieldSmall}
        value={JSON.stringify(entry?.queries ?? {}, null, 2)}
        onChange={handleQueriesChange}
      />
    </div>
  );
}
